// Token persistence, shared by AuthContext and the axios interceptors.
//
// Both tokens live in localStorage so every tab of the same origin sees the
// same session. sessionStorage would scope the access token to one tab, which
// forced a fresh login in every new tab.
//
// The refresh endpoint rotates the refresh token and the server keeps only one
// hash per user, so two tabs refreshing independently invalidate each other.
// The lock below serialises refreshes across tabs: the winner refreshes, the
// losers wait for the new access token to arrive via the `storage` event.

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const REFRESH_LOCK_KEY = 'refreshLock';

/** How long a refresh lock stays valid before another tab may take over. */
export const REFRESH_LOCK_TTL_MS = 10_000;

const tabId = Math.random().toString(36).slice(2);

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/** Runs `onChange` when another tab writes or clears a token. Returns an unsubscribe. */
export function subscribeToTokenChanges(onChange: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === null || e.key === ACCESS_TOKEN_KEY || e.key === REFRESH_TOKEN_KEY) {
      onChange();
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/**
 * Best-effort cross-tab lock. localStorage has no compare-and-swap, so two tabs
 * reading at the same instant can both win — that is the behaviour we have
 * today, and the expired-lock takeover keeps a crashed tab from blocking the app.
 */
export function acquireRefreshLock(): boolean {
  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  if (raw) {
    try {
      const { at } = JSON.parse(raw) as { owner: string; at: number };
      if (Date.now() - at < REFRESH_LOCK_TTL_MS) return false;
    } catch {
      // Corrupt lock — fall through and take it.
    }
  }
  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ owner: tabId, at: Date.now() }));
  return true;
}

/** Only clears a lock this tab owns — releasing someone else's would let two tabs refresh at once. */
export function releaseRefreshLock(): void {
  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  if (!raw) return;
  try {
    const { owner } = JSON.parse(raw) as { owner: string; at: number };
    if (owner !== tabId) return;
  } catch {
    // Corrupt lock — clearing it is the useful thing to do.
  }
  localStorage.removeItem(REFRESH_LOCK_KEY);
}

/**
 * Waits for the tab holding the lock to publish a new access token. Resolves
 * with the new token, or null if nothing arrived within `timeoutMs`.
 */
export function waitForAccessToken(previous: string | null, timeoutMs: number): Promise<string | null> {
  const current = getAccessToken();
  if (current && current !== previous) return Promise.resolve(current);

  return new Promise(resolve => {
    const finish = (value: string | null) => {
      window.removeEventListener('storage', handler);
      clearTimeout(timer);
      resolve(value);
    };
    const handler = (e: StorageEvent) => {
      if (e.key !== ACCESS_TOKEN_KEY) return;
      if (e.newValue && e.newValue !== previous) finish(e.newValue);
      else if (e.newValue === null) finish(null); // other tab logged out
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('storage', handler);
  });
}
